// srcs/frontend/src/components/proposal/pages/SeedsPage.tsx
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useProposal } from '../state/ProposalContext.tsx';

import {
  useSeedsSearchMutation,
  useSeedsRankingMutation,
  useLazyGetDataQuery,
  useClearDataMutation,
  useImportFileMutation,
  useLazyGetFileQuery,
} from '../../../services/solutionAPI.ts';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 5 * 60 * 1000;

type SeedsRow = {
  id: string;
  seed_id: string;
  seed_title: string;
  provider: string;
  tech_domain: string;
  solvable_issues: string[];
  case_studies: string[];
  similarity_score: number;
  implementation: number;
  timeliness: number;
  cost: number;
  evolvability: number;
  credibility: number;
  weightedScore?: number;
  rank?: number;
  function_overview?: string;
  differentiation_points?: string;
  implementation_requirements?: string;
  cost_structure?: string;
  maturity_level?: string;
};

// /files に保存するペイロードの型（復元にも使用）
type SeedsExportPayload = {
  data_type: 'seeds';
  session_id: string;
  seed_id: 'NULL';
  company_info: {
    company_name: string;
    industry: string;
    company_size: string;
  };
  needs_info: {
    problem: string;
    ideal_state: string;
    past_efforts: string;
    deadline: string;
    budget: string;
  };
  options: {
    ai_query_expansion: boolean;
    top_k: number;
    alap_flg: boolean | null;
  };
  weights: {
    wSimilarity: number;
    wImplementation: number;
    wTimeliness: number;
    wCost: number;
    wEvolvability: number;
    wCredibility: number;
  };
  // 左側の完全復元のために選択中シーズも保存
  selected_seed_id: string | null;
  selected_seed_info: {
    seed_id: string;
    seed_title: string;
    provider: string;
    tech_domain: string;
    solvable_issues: string[];
    case_studies: string[];
    function_overview?: string;
    differentiation_points?: string;
    implementation_requirements?: string;
    cost_structure?: string;
    maturity_level?: string;
  } | null;

  // 検索結果の配列（weightedScore / rank 含む）
  results: SeedsRow[];

  updated_at: number;
};

function isFinishedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'finished' || s === '完了';
}
function isFailedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'failed' || s === '失敗';
}

function toList(v: any): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String).map((x) => x.trim()).filter(Boolean);
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s || s.toLowerCase() === 'n/a') return [];
    return s.split(/[\n,、]/g).map((x) => x.trim()).filter(Boolean);
  }
  return [String(v)].map((x) => x.trim()).filter(Boolean);
}

function pickItemsArray(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data.items)) return data.items;

  // "items(シーズ検索の例)" のようなキーに対応
  for (const k of Object.keys(data)) {
    if (k.startsWith('items') && Array.isArray((data as any)[k])) return (data as any)[k];
  }
  return [];
}

function normalizeSeedsDataToRows(data: any): SeedsRow[] {
  const items = pickItemsArray(data);
  if (!Array.isArray(items)) return [];

  return items
    .map((it: any, idx: number) => {
      const s = it?.seed_info ?? it?.seed ?? it;
      if (!s) return null;

      const seedId = String(s.seed_id ?? s.id ?? `seed-${idx}`);
      const title = String(s.title ?? s.seed_title ?? '—');
      const ranking = Number(s.ranking ?? s.rank ?? (idx + 1));

      const score = s.score_info ?? {};
      const detail = s.detailed_info ?? {};

      return {
        id: seedId,
        seed_id: seedId,
        seed_title: title,
        provider: String(s.provider ?? ''),
        tech_domain: String(detail.technical_field ?? s.technical_field ?? s.tech_domain ?? ''),
        solvable_issues: toList(detail.solvable_issues ?? s.solvable_issues),
        case_studies: toList(detail.case_studies ?? s.case_studies),
        similarity_score: Number(score.similarity ?? s.similarity_score ?? 0),
        implementation: Number(score.implementability ?? s.implementation ?? 0),
        timeliness: Number(score.time_compatibility ?? s.timeliness ?? 0),
        cost: Number(score.cost_compatibility ?? s.cost ?? 0),
        evolvability: Number(score.scalability ?? s.evolvability ?? 0),
        credibility: Number(score.reliability ?? s.credibility ?? 0),
        rank: Number.isFinite(ranking) ? ranking : (idx + 1),
        function_overview: String(detail.function_overview ?? s.function_overview ?? ''),
        differentiation_points: String(detail.differentiation_points ?? s.differentiation_points ?? ''),
        implementation_requirements: String(detail.implementation_requirements ?? s.implementation_requirements ?? ''),
        cost_structure: String(detail.cost_structure ?? s.cost_structure ?? ''),
        maturity_level: String(detail.maturity_level ?? s.maturity_level ?? ''),
      } as SeedsRow;
    })
    .filter(Boolean) as SeedsRow[];
}

export default function SeedsPage() {
  const {
    sessionId,
    setSessionId,
    selectedSeedId,
    setSelectedSeedId,
    selectedSeedInfo,
    setCompanyInfo,
    setNeedsInfo,
    setSelectedSeedInfo,
  } = useProposal();

  const [seedsSearch] = useSeedsSearchMutation();
  const [_seedsRanking] = useSeedsRankingMutation();
  const [triggerGetData] = useLazyGetDataQuery();
  const [clearData] = useClearDataMutation();
  const [postFiles] = useImportFileMutation();      // POST /files
  const [triggerGetFile] = useLazyGetFileQuery();   // GET /files

  // 左：入力
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');

  const [problem, setProblem] = useState('');
  const [idealState, setIdealState] = useState('');
  const [pastEfforts, setPastEfforts] = useState('');
  const [deadline, setDeadline] = useState('');
  const [budget, setBudget] = useState('');

  // option
  const [aiExpansion, setAiExpansion] = useState(true);
  const [topK, setTopK] = useState(20);
  const [alapFlg, setAlapFlg] = useState<boolean | null>(null);

  // weights
  const [wSimilarity, setWSimilarity] = useState(1.0);
  const [wImplementation, setWImplementation] = useState(1.0);
  const [wTimeliness, setWTimeliness] = useState(1.0);
  const [wCost, setWCost] = useState(1.0);
  const [wEvolvability, setWEvolvability] = useState(1.0);
  const [wCredibility, setWCredibility] = useState(1.0);

  // results
  const [rows, setRows] = useState<SeedsRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  // ★ 初回マウント時の再取得ガード（1回だけ）
  const [initialRefetched, setInitialRefetched] = useState(false);
  const [canAutoSave, setCanAutoSave] = useState(false);

  const allTextFilled = useMemo(() => {
    const fields = [companyName, industry, companySize, problem, idealState, pastEfforts, deadline, budget];
    return fields.every((v) => v.trim().length > 0);
  }, [companyName, industry, companySize, problem, idealState, pastEfforts, deadline, budget]);

  const canSearch = allTextFilled && !loading;
  const canApplyWeights = Array.isArray(rows) && rows.length > 0 && !loading;

  function applyWeightsLocal(input: SeedsRow[]): SeedsRow[] {
    const w = {
      similarity: wSimilarity,
      implementation: wImplementation,
      timeliness: wTimeliness,
      cost: wCost,
      evolvability: wEvolvability,
      credibility: wCredibility,
    };
    const scored = input.map((r) => {
      const ws =
        (r.similarity_score ?? 0) * w.similarity +
        (r.implementation ?? 0) * w.implementation +
        (r.timeliness ?? 0) * w.timeliness +
        (r.cost ?? 0) * w.cost +
        (r.evolvability ?? 0) * w.evolvability +
        (r.credibility ?? 0) * w.credibility;
      return { ...r, weightedScore: Math.round(ws * 10) / 10 };
    });
    scored.sort((a, b) => (b.weightedScore ?? 0) - (a.weightedScore ?? 0));
    return scored.map((r, idx) => ({ ...r, rank: idx + 1 }));
  }

  // ===== /files 保存（現状の左ペイン＋選択＋結果を保存） =====
  async function exportSeedsNow(reason: string, sid: string, rowsForSave: SeedsRow[] | null, override?: { selectedId?: string; selectedInfo?: ReturnType<typeof toSelectedInfo> }, force = false) {
    if (!force && !canAutoSave) {
      return;
    }
    const payload: SeedsExportPayload = {
      data_type: 'seeds',
      session_id: sid,
      seed_id: 'NULL',
      company_info: { company_name: companyName, industry, company_size: companySize },
      needs_info: { problem, ideal_state: idealState, past_efforts: pastEfforts, deadline, budget },
      options: { ai_query_expansion: aiExpansion, top_k: topK, alap_flg: alapFlg },
      weights: { wSimilarity, wImplementation, wTimeliness, wCost, wEvolvability, wCredibility },
      selected_seed_id: override?.selectedId ?? selectedSeedId ?? null,
      selected_seed_info: override?.selectedInfo ?? (selectedSeedInfo
        ? {
          seed_id: selectedSeedInfo.seed_id,
          seed_title: selectedSeedInfo.seed_title,
          provider: selectedSeedInfo.provider,
          tech_domain: selectedSeedInfo.tech_domain,
          solvable_issues: selectedSeedInfo.solvable_issues ?? [],
          case_studies: selectedSeedInfo.case_studies ?? [],
          function_overview: selectedSeedInfo.function_overview,
          differentiation_points: selectedSeedInfo.differentiation_points,
          implementation_requirements: selectedSeedInfo.implementation_requirements,
          cost_structure: selectedSeedInfo.cost_structure,
          maturity_level: selectedSeedInfo.maturity_level,
        }
        : null),
      results: rowsForSave ?? [],
      updated_at: Date.now(),
    };
    const file_name = `seeds_${sid}_NULL.json`;
    try {
      await postFiles({ file_name, file_context: JSON.stringify(payload) }).unwrap();
    } catch {/* ignore */ }
  }

  // ===== /files 復元（選択上書きのガード付き） =====
  async function importSeedsFromFiles(sid: string, reason: string) {
    const file_name = `seeds_${sid}_NULL.json`;
    try {
      const res: any = await triggerGetFile({ file_name }).unwrap();
      const raw = res?.file_context ?? res?.items ?? res?.content ?? res;
      const payload: SeedsExportPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!payload || payload.data_type !== 'seeds') return;

      // 1) 左ペイン（入力）
      setCompanyName(payload.company_info?.company_name ?? '');
      setIndustry(payload.company_info?.industry ?? '');
      setCompanySize(payload.company_info?.company_size ?? '');
      setProblem(payload.needs_info?.problem ?? '');
      setIdealState(payload.needs_info?.ideal_state ?? '');
      setPastEfforts(payload.needs_info?.past_efforts ?? '');
      setDeadline(payload.needs_info?.deadline ?? '');
      setBudget(payload.needs_info?.budget ?? '');

      // 2) オプション
      setAiExpansion(Boolean(payload.options?.ai_query_expansion));
      setTopK(Number.isFinite(payload.options?.top_k) ? Number(payload.options.top_k) : 20);
      setAlapFlg(payload.options?.alap_flg === null ? null : (payload.options?.alap_flg as boolean));

      // 3) 重み
      if (payload.weights) {
        setWSimilarity(Number(payload.weights.wSimilarity ?? 1));
        setWImplementation(Number(payload.weights.wImplementation ?? 1));
        setWTimeliness(Number(payload.weights.wTimeliness ?? 1));
        setWCost(Number(payload.weights.wCost ?? 1));
        setWEvolvability(Number(payload.weights.wEvolvability ?? 1));
        setWCredibility(Number(payload.weights.wCredibility ?? 1));
      }

      // 4) 検索結果（保存時のランクを維持）
      setRows(Array.isArray(payload.results) ? payload.results : []);

      // 5) Context（下流で利用）
      setCompanyInfo({
        company_name: payload.company_info?.company_name ?? '',
        industry: payload.company_info?.industry ?? '',
        company_size: payload.company_info?.company_size ?? '',
      });
      setNeedsInfo({
        problem: payload.needs_info?.problem ?? '',
        ideal_state: payload.needs_info?.ideal_state ?? '',
        past_efforts: payload.needs_info?.past_efforts ?? '',
        deadline: payload.needs_info?.deadline ?? '',
        budget: payload.needs_info?.budget ?? '',
      });

      // 6) 選択中シーズ：**画面に既に選択があれば上書きしない**
      if (!selectedSeedId && payload.selected_seed_id && payload.selected_seed_info) {
        setSelectedSeedId(payload.selected_seed_id);
        setSelectedSeedInfo(payload.selected_seed_info);
      }
      setCanAutoSave(true);
    } catch {/* no saved file yet */ }
  }

  // ===== /data ポーリング（finished→表示→保存） =====
  const pollSeedsData = async (sid: string, reason: string) => {
    const start = Date.now();
    let attempt = 0;

    while (Date.now() - start < POLL_MAX_MS) {
      attempt += 1;
      try {
        const data = await triggerGetData({ session_id: sid, data_type: 'seeds' as const, seed_id: 'NULL' }).unwrap();

        if (isFinishedStatus(data?.status)) {
          const ranked = applyWeightsLocal(normalizeSeedsDataToRows(data));
          setRows(ranked);
          setCanAutoSave(true);
          await exportSeedsNow(
            `poll_finished:${reason}`,
            sid,
            ranked,
            undefined,
            true
          );
          return;
        }
        if (isFailedStatus(data?.status)) {
          setRows([]);
          return;
        }
      } catch {/* retry */ }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    setRows([]);
  };

  // ★ 初回マウント専用：最大 maxMs だけ /data をポーリング（保存しない）
  const pollSeedsDataWithTimeout = async (
    sid: string,
    reason: string,
    maxMs: number
  ) => {
    const start = Date.now();
    let attempt = 0;

    while (Date.now() - start < maxMs) {
      attempt += 1;
      try {
        const data = await triggerGetData({
          session_id: sid,
          data_type: 'seeds' as const,
          seed_id: 'NULL',
        }).unwrap();

        if (isFinishedStatus(data?.status)) {
          const ranked = applyWeightsLocal(normalizeSeedsDataToRows(data));
          setRows(ranked);
          return; // ★ 初回なので export はしない
        }

        if (isFailedStatus(data?.status)) {
          return;
        }
      } catch {
        // retry
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  };

  // ★ 初回マウント時：sessionId があれば必ず再取得（最大10秒）
  React.useEffect(() => {
    if (!sessionId) return;
    if (initialRefetched) return;

    (async () => {
      // 1) まず /files から復元
      await importSeedsFromFiles(sessionId, 'initial_mount');

      // 2) 次に /data を最大10秒だけ取りに行く
      await pollSeedsDataWithTimeout(sessionId, 'initial_mount', 10_000);

      setInitialRefetched(true);
    })();
  }, [sessionId, initialRefetched]);

  // ===== 検索実行 =====
  const onRunSearch = async () => {
    if (!canSearch) return;
    setCanAutoSave(true);

    setLoading(true);
    setRows(null);
    setSelectedSeedId(null);
    setSelectedSeedInfo(null);

    // Context保存（GAP/ROI用）
    setCompanyInfo({ company_name: companyName, industry, company_size: companySize });
    setNeedsInfo({ problem, ideal_state: idealState, past_efforts: pastEfforts, deadline, budget });

    const body = {
      company_info: { company_name: companyName, industry, company_size: companySize },
      needs_info: { problem, ideal_state: idealState, past_efforts: pastEfforts, deadline, budget },
      ai_query_expansion: aiExpansion,
      top_k: Math.max(1, Math.min(20, topK)),
      ...(alapFlg !== null ? { alap_flg: alapFlg } : {}),
    };

    try {
      // 2回目以降はセッション全クリア（session_id のみ）
      if (sessionId) {
        await clearData({ session_id: sessionId } as any).unwrap();
        setSessionId(null);
      }

      const res = await seedsSearch(body).unwrap(); // accepted 期待
      const newSid = res?.session_id;

      if (newSid) {
        setSessionId(newSid);
        await pollSeedsData(newSid, 'after_search'); // 表示更新 → 自動保存
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== 再取得：/files 復元 → /data 最新化 =====
  const onRefetch = async () => {
    if (!sessionId) return;
    setLoading(true);
    setRows(null);

    try {
      await importSeedsFromFiles(sessionId, 'refetch'); // 左ペイン・結果を先に戻す（選択は保持）
      await pollSeedsData(sessionId, 'refetch');        // 最新 /data を反映（自動保存も走る）
    } finally {
      setLoading(false);
    }
  };

  // ===== 手動保存 =====
  const onSaveNow = async () => {
    if (!sessionId) return;
    await exportSeedsNow('manual_save', sessionId, rows);
  };

  // ===== 重み適用（ローカル並べ替え → 保存）=====
  const onApplyWeights = async () => {
    if (!canApplyWeights || !rows) return;
    setLoading(true);
    try {
      const reranked = applyWeightsLocal(rows);
      setRows(reranked);
      if (sessionId) await exportSeedsNow('weights_applied', sessionId, reranked);
    } finally {
      setLoading(false);
    }
  };

  // ===== シーズ選択時：set のみ + その場でスナップショット保存（POST /files）=====
  function toSelectedInfo(r: SeedsRow) {
    return {
      seed_id: r.seed_id,
      seed_title: r.seed_title,
      provider: r.provider,
      tech_domain: r.tech_domain,
      solvable_issues: r.solvable_issues ?? [],
      case_studies: r.case_studies ?? [],
      function_overview: r.function_overview,
      differentiation_points: r.differentiation_points,
      implementation_requirements: r.implementation_requirements,
      cost_structure: r.cost_structure,
      maturity_level: r.maturity_level,
    } as const;
  }

  async function onSelectSeed(r: SeedsRow) {
    // 1) 画面状態を更新（ネットワークは叩かない）
    setSelectedSeedId(r.seed_id);
    setSelectedSeedInfo(toSelectedInfo(r));

    // 2) その場の“左ペイン＋選択＋結果”を /files にスナップショット保存
    if (sessionId) {
      await exportSeedsNow('selection_changed', sessionId, rows, {
        selectedId: r.seed_id,
        selectedInfo: toSelectedInfo(r),
      });
    }
  }

  return (
    <Box sx={{ height: 'calc(100vh - 32px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, p: 2 }}>
      {/* 左：入力 */}
      <Box sx={{ overflow: 'auto', pr: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>シーズ検索</Typography>

        <Stack spacing={1.2} sx={{ maxWidth: 640, mb: 2 }}>
          <TextField label="企業名 *" size="small" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          <TextField label="業種 *(運送業、電子機器…)" size="small" value={industry} onChange={(e) => setIndustry(e.target.value)} />
          <TextField label="企業規模 *(大企業、中小企業…)" size="small" value={companySize} onChange={(e) => setCompanySize(e.target.value)} />

          <TextField label="顧客困りごと *" size="small" multiline minRows={3} value={problem} onChange={(e) => setProblem(e.target.value)} />
          <TextField label="理想とする状態 *" size="small" multiline minRows={2} value={idealState} onChange={(e) => setIdealState(e.target.value)} />
          <TextField label="これまでの取り組み *" size="small" multiline minRows={2} value={pastEfforts} onChange={(e) => setPastEfforts(e.target.value)} />

          <Grid container spacing={1}>
            <Grid item xs={6}><TextField label="導入期限 *(yyyy-mm-dd)" size="small" value={deadline} onChange={(e) => setDeadline(e.target.value)} /></Grid>
            <Grid item xs={6}><TextField label="予算感 *" size="small" value={budget} onChange={(e) => setBudget(e.target.value)} /></Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={aiExpansion} onChange={(_, v) => setAiExpansion(v)} />}
                label="AIクエリ拡張"
              />
              <Typography variant="caption" color="text.secondary">※シーズには必須です</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">上位: {topK}件　※件数に比例して時間がかかります</Typography>
              <Slider min={1} max={20} step={1} value={topK} onChange={(_, v) => setTopK(v as number)} />
            </Grid>
            <Grid item xs={12}>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="alap-label">ALAPフィルタ（alap_flg）</InputLabel>
                <Select
                  labelId="alap-label"
                  label="ALAPフィルタ（alap_flg）"
                  value={alapFlg === null ? 'omit' : alapFlg ? 'true' : 'false'}
                  onChange={(e: SelectChangeEvent) => {
                    const v = e.target.value;
                    setAlapFlg(v === 'omit' ? null : v === 'true');
                  }}
                >
                  <MenuItem value="omit">フィルタなし（送信しない）</MenuItem>
                  <MenuItem value="true">ALAPのみ（true）</MenuItem>
                  <MenuItem value="false">非ALAPのみ（false）</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" disabled={!canSearch} onClick={onRunSearch}>
            検索実行
          </Button>
        </Stack>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>⚖️ 重み設定</Typography>

        <Stack spacing={2} sx={{ maxWidth: 640 }}>
          <WeightRow label="類似度" value={wSimilarity} onChange={setWSimilarity} />
          <WeightRow label="実装可能性" value={wImplementation} onChange={setWImplementation} />
          <WeightRow label="時間適合性" value={wTimeliness} onChange={setWTimeliness} />
          <WeightRow label="コスト適合性" value={wCost} onChange={setWCost} />
          <WeightRow label="発展性" value={wEvolvability} onChange={setWEvolvability} />
          <WeightRow label="信頼性" value={wCredibility} onChange={setWCredibility} />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="contained" color="primary" disabled={!canApplyWeights} onClick={onApplyWeights}>
            重み適用
          </Button>
          <Button variant="text" onClick={() => {
            setWSimilarity(1); setWImplementation(1); setWTimeliness(1);
            setWCost(1); setWEvolvability(1); setWCredibility(1);
          }}>
            重みリセット
          </Button>
        </Stack>
      </Box>

      {/* 右：結果 */}
      <Box sx={{ overflow: 'auto', pl: 1 }}>
        {/* 右上 sticky：再取得＋保存 */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, pb: 1, bgcolor: 'background.default' }}>
          <Button variant="outlined" size="small" disabled={!sessionId || loading} onClick={onRefetch}>
            再取得
          </Button>
          <Button variant="outlined" size="small" disabled={!sessionId || loading || rows === null} onClick={onSaveNow}>
            保存
          </Button>
        </Box>

        {loading && (
          <Box sx={{ height: 'calc(100vh - 140px)', display: 'grid', placeItems: 'center' }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">取得中…</Typography>
            </Stack>
          </Box>
        )}

        {!loading && rows === null && <Box />}

        {!loading && rows !== null && (
          <Box>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  検索結果（{rows.length}件）
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Chip size="small" label={sessionId ? `sessionId: ${sessionId}` : 'sessionId: —'} />
                  <Chip size="small" color={selectedSeedId ? 'success' : 'default'} label={selectedSeedId ? `選択中 seed_id: ${selectedSeedId}` : '選択中 seed_id: —'} />
                </Stack>
              </Stack>
            </Paper>

            {rows.length === 0 && (
              <Alert severity="info">結果は0件でした。条件を変えて再検索してください。</Alert>
            )}

            {rows.map((r) => (
              <Accordion key={r.id} disableGutters sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack sx={{ width: '100%' }} spacing={0.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {r.rank ? `第${r.rank}位: ` : ''}{r.seed_title}
                      </Typography>
                      <Chip
                        size="small"
                        label={r.weightedScore != null ? `総合: ${r.weightedScore}` : '総合: -'}
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                      <Typography variant="caption" color="text.secondary">seed_id: {r.seed_id}</Typography>
                      <Typography variant="caption" color="text.secondary">提供元: {r.provider || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">技術領域: {r.tech_domain || '—'}</Typography>
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={4}><ScoreBox label="類似度" value={r.similarity_score} color="#e3f2fd" /></Grid>
                    <Grid item xs={6} sm={4}><ScoreBox label="実装可能性" value={r.implementation} color="#e8f5e9" /></Grid>
                    <Grid item xs={6} sm={4}><ScoreBox label="時間適合性" value={r.timeliness} color="#fff3e0" /></Grid>
                    <Grid item xs={6} sm={4}><ScoreBox label="コスト適合性" value={r.cost} color="#fce4ec" /></Grid>
                    <Grid item xs={6} sm={4}><ScoreBox label="発展性" value={r.evolvability} color="#f3e5f5" /></Grid>
                    <Grid item xs={6} sm={4}><ScoreBox label="信頼性" value={r.credibility} color="#e0f2f1" /></Grid>
                  </Grid>

                  <Accordion disableGutters sx={{ mb: 1 }} defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 700 }}>詳細情報を表示</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ mb: 1 }}><b>概要：</b> {r.function_overview || '—'}</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>解決可能課題：</b><br />
                        {(r.solvable_issues?.length ? r.solvable_issues : ['—']).map((x, i) => (
                          <span key={i}>・{x}<br /></span>
                        ))}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>導入事例：</b><br />
                        {(r.case_studies?.length ? r.case_studies : ['—']).map((x, i) => (
                          <span key={i}>・{x}<br /></span>
                        ))}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant={selectedSeedId === r.seed_id ? 'contained' : 'outlined'}
                      onClick={() => onSelectSeed(r)} // ← set だけ + /files に即保存
                    >
                      {selectedSeedId === r.seed_id ? '選択中' : 'このシーズを選択'}
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function WeightRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ width: 140, fontWeight: 600 }}>{label}</Box>
      <Slider
        min={0.1}
        max={5}
        step={0.1}
        value={value}
        onChange={(_, v) => onChange(v as number)}
        valueLabelDisplay="auto"
        sx={{ maxWidth: 360 }}
      />
      <Box sx={{ width: 48, textAlign: 'right' }}>{value.toFixed(1)}x</Box>
    </Stack>
  );
}

function ScoreBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Box sx={{ bgcolor: color, p: 1, borderRadius: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {label}: {value}
      </Typography>
    </Box>
  );
}